import { Box, Popover, Typography } from '@mui/material';
import React, { MouseEvent, SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import OverflowMore from '../../../../utils/layout/OverflowMore';
import { KEY_CREDITS_POPOVER_DEFER_MS } from '../../../constants';
import type { KeyCreditsWrapLimit } from '../../../types';

type KeyCreditsWrapLimitReadProps = {
    text: string;
    previewText?: string;
    wrapLimit: KeyCreditsWrapLimit;
    isLineBreakMode?: boolean;
    onStartEdit: (e: MouseEvent) => void;
    totalCount?: number;
};

/** Editable + wrap limit: deferred popover on single click; double-click opens grid edit. */
export default function KeyCreditsWrapLimitRead({
    text,
    previewText,
    wrapLimit,
    isLineBreakMode = false,
    onStartEdit,
    totalCount
}: KeyCreditsWrapLimitReadProps) {
    const anchorRef = useRef<HTMLElement | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const clearTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    useEffect(() => () => clearTimer(), [clearTimer]);

    const startEdit = (e: MouseEvent) => {
        clearTimer();
        setAnchorEl(null);
        onStartEdit(e);
    };

    const onClick = (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        if (e.detail >= 2) {
            startEdit(e);
            return;
        }
        clearTimer();
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            if (anchorRef.current) setAnchorEl(anchorRef.current);
        }, KEY_CREDITS_POPOVER_DEFER_MS);
    };

    const remainingCount = totalCount == null ? 0 : Math.max(0, totalCount - wrapLimit);
    const showMoreIndicator = isLineBreakMode && remainingCount > 0;

    return (
        <>
            <Box
                ref={anchorRef}
                className="clickable"
                onClick={onClick}
                onDoubleClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    startEdit(e);
                }}
                sx={{
                    wordBreak: 'break-word',
                    width: '100%',
                    cursor: 'pointer'
                }}
            >
                <Box component="span" sx={{ ...(isLineBreakMode ? { whiteSpace: 'pre-line' } : {}) }}>
                    {previewText ?? text}
                </Box>
                {showMoreIndicator && (
                    <OverflowMore
                        count={remainingCount}
                        variant="inherit"
                        sx={{
                            color: 'text.primary'
                        }}
                    />
                )}
            </Box>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={(e: SyntheticEvent) => {
                    e.stopPropagation();
                    setAnchorEl(null);
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                slotProps={{ paper: { sx: { maxWidth: 400 } } }}
            >
                <Typography sx={{ p: 2, whiteSpace: isLineBreakMode ? 'pre-line' : undefined }}>{text}</Typography>
            </Popover>
        </>
    );
}
